<i18n>
{
	"en": {
		"addToTrip": "Add to Trip",
		"newOrder": "New Order",
		"ordersFound": "No orders | {count} order | {count} orders",
		"addToTransporter": "Add to External",
		"noOrdersFound": "No orders were found that match this search. Try expanding your search criteria.",
		"failedToGeocode": "Failed to geocode the selected orders",
		"packageLabel": "Labels",
		"waybill": "Waybill",
		"parallelSubOrderCreated": "Parallel created",
		"unableToCreateParallelSubOrder": "Unable to create parallel"
	},
	"no": {
		"addToTrip": "Legg til på tur",
		"newOrder": "Nytt oppdrag",
		"ordersFound": "Ingen oppdrag | {count} tilgjengelig oppdrag | {count} oppdrag",
		"addToTransporter": "Ekstern leverandør",
		"noOrdersFound": "Vi fant ingen oppdrag som matcher dette søket. Prøv å utvide søkekriteriene.",
		"failedToGeocode": "Kunne ikke geokode oppdrag",
		"packageLabel": "Etiketter",
		"waybill": "Fraktbrev",
		"parallelSubOrderCreated": "Parallell strekning laget",
		"unableToCreateParallelSubOrder": "Kunne ikke lage parallel strekning"
	}
}
</i18n>
<template>
	<t-vertical-panel class="order-list">
		<div slot="header" class="order-list-header">
			<t-icon-button
				v-if="!filtersVisible"
				icon="filter-open"
				color="blue"
				iconSize="sm"
				type="bp"
				style="margin-right: 5px"
				@click="$emit('toggleFilters')"
			/>
			<t-icon-button
				v-if="!filtersVisible"
				icon="search"
				color="blue"
				iconSize="sm"
				type="bp"
				style="margin-right: 15px"
				@click="$emit('search')"
			/>

			<t-panel-header icon="orders" class="order-list-panel-header">
				{{ $tc('ordersFound', !ordersLoading && ordersFetching ? totals.orders : ordersFound) }}
			</t-panel-header>
			<div class="order-list-summary">
				<t-metric-details
					:value="displayedVolume"
					decimals="1"
					hide-pie-icon
					unit="m3"
				/>
				<t-metric-details
					:value="displayedWeight"
					hide-pie-icon
					unit="kg"
				/>
				<t-metric-details
					:value="displayedLoadingMeters"
					decimals="1"
					hide-pie-icon
					unit="lm"
				/>
				<t-metric-details
					:value="displayedTotalPrice"
					decimals="2"
					hide-pie-icon
					:unit="totals.baseCurrency"
				/>
			</div>
			<div style="padding-left: 10px; padding-right: 10px; cursor: pointer;" @click="toggleOrderList()">
				<t-timpex-icon icon="minimize" color="blue" type="bp" />
			</div>
		</div>
		<div slot="sub-header" ref="subHeader" class="order-list-sub-header">
			<div v-if="!smallSubHeader" class="buttons-with-text">
				<t-button
					:value="$t('newOrder')"
					appearance="minimal"
					type="bp"
					iconBefore="plus"
					@click="openNewOrderModal"
				/>
				<t-button
					:value="$t('packageLabel')"
					:disabled="rowsSelected === 0"
					appearance="minimal"
					type="timpex"
					iconBefore="package-label"
					@click="showPackageLabel"
				/>
				<t-button
					:value="$t('waybill')"
					:disabled="rowsSelected === 0"
					appearance="minimal"
					type="timpex"
					iconBefore="waybill"
					@click="showWaybill"
				/>
				<div/>
				<t-button
					:value="$t('addToTransporter')"
					:disabled="!addToTransporterEnabled"
					appearance="minimal"
					type="bp"
					iconBefore="new-link"
					@click="$emit('showAddToTransporter', {orders: selectedOrders, trip: tripDetails})"
				/>
				<t-button
					:value="$t('addToTrip')"
					:disabled="!addToTripEnabled"
					appearance="minimal"
					type="bp"
					iconBefore="add-to-artifact"
					@click="$emit('showAddToTrip', {orders: selectedOrders, trip: tripDetails})"
				/>
			</div>
			<div v-if="smallSubHeader" class="buttons-no-text">
				<t-icon-button
					:value="$t('newOrder')"
					icon="plus"
					iconSize="sm"
					appearance="minimal"
					type="bp"
					@click="openNewOrderModal"
				/>
				<t-icon-button
					:value="$t('packageLabel')"
					:disabled="rowsSelected === 0"
					icon="package-label"
					iconSize="sm"
					appearance="minimal"
					type="timpex"
					@click="showPackageLabel"
				/>
				<t-icon-button
					:value="$t('waybill')"
					:disabled="rowsSelected === 0"
					icon="waybill"
					iconSize="sm"
					appearance="minimal"
					type="timpex"
					@click="showWaybill"
				/>
				<div/>
				<t-button
					:value="$t('addToTransporter')"
					:disabled="!addToTransporterEnabled"
					appearance="minimal"
					type="bp"
					iconBefore="new-link"
					@click="$emit('showAddToTransporter', {orders: selectedOrders, trip: tripDetails})"
				/>
				<t-button
					:value="$t('addToTrip')"
					:disabled="!addToTripEnabled"
					appearance="minimal"
					type="bp"
					iconBefore="add-to-artifact"
					@click="$emit('showAddToTrip', {orders: selectedOrders, trip: tripDetails})"
				/>
			</div>
		</div>

		<div class="order-list-content">
			<order-grid
				v-show="ordersFound"
				ref="grid"
				:orders="displayedOrders"
				:localStorageId="localStorageId"
				selectable
				@selected="onRowsSelected"
				@filtered="onFiltered"
				@geocode="onGeocodeOrder"
				@packageLabel="showSinglePackageLabel"
				@waybill="showSingleWaybill"
				@parallelSuborder="onCreateParallelSubOrder"
			/>

			<t-no-content-panel v-show="!ordersFound" :text="$t('noOrdersFound')" icon="order" />
			<t-overlay
				:isShown="ordersLoading"
				loaderMarginBottom="50px"
				absolute
				loader
			/>
		</div>
	</t-vertical-panel>
</template>

<script>
import OrderGrid from './OrderGrid';
import {
	mapDynamicState, mapDynamicGetters, mapDynamicActions
} from '@/core/mapDynamic';
import { eventBus, sumValues } from '@/core';
import TMetricDetails from '@/components/evergreen/timpex/layers/TMetricDetails';

export default {
	name: 'OrderSearchMapGrid',
	inject: ['namespace'],
	components: { TMetricDetails, OrderGrid },
	props: { filtersVisible: Boolean },
	data() {
		return {
			rowsSelected: 0,
			mapPosition: null,
			observer: null,
			subHeaderObserver: null,
			subHeaderWidth: null
		};
	},
	computed: {
		...mapDynamicState('computedStoreNamespace', [
			'orders',
			'ordersLoading',
			'ordersFetching',
			'totals'
		]),
		...mapDynamicState('computedLoadPlanningNamespace', ['filteredGridOrderIds',]),
		...mapDynamicGetters('computedLoadPlanningNamespace', [
			'displayedOrders',
			'filteredOrders',
		]),
		...mapDynamicState('computedTripNamespace', ['tripDetails']),
		computedStoreNamespace() {
			return `loadPlanning/${this.namespace.id}/orderSearch`;
		},
		computedTripNamespace() {
			return `loadPlanning/${this.namespace.id}/tripSearch`;
		},
		computedLoadPlanningNamespace() {
			return `loadPlanning/${this.namespace.id}`;
		},
		localStorageId() {
			return `loadPlanningOrderSearchList/${this.namespace.id}`;
		},
		ordersFound() {
			return this.filteredOrders.length;
		},
		singleOrderSelected() {
			return this.rowsSelected === 1;
		},
		addToTransporterEnabled() {
			return this.rowsSelected > 0 && this.selectedOrders.every(x => !x.tripId && !x.external);
		},
		addToTripEnabled() {
			return this.rowsSelected > 0 && this.tripDetails && this.tripDetails.id && this.tripDetails.type.internalCode !== 'external' && this.selectedOrders.every(x => !x.tripId && !x.external);
		},
		selectedOrders() {
			if (this.rowsSelected > 0) {
				return this.$refs.grid.getSelectedItems();
			}
			return [];
		},
		smallSubHeader() {
			return this.subHeaderWidth < 600;
		},
		totalVolume() {
			return this.ordersFetching ? this.totals.volume : sumValues(this.filteredOrders, 'volume');
		},
		totalWeight() {
			return this.ordersFetching ? this.totals.weight : sumValues(this.filteredOrders, 'grossWeight');
		},
		totalLoadingMeters() {
			return this.ordersFetching ? this.totals.loadingMeters : sumValues(this.filteredOrders, 'loadingMeters');
		},
		totalPrice() {
			return this.ordersFetching ? this.totals.totalPrice : sumValues(this.orders, 'totalPrice');
		},
		displayedVolume() {
			return this.rowsSelected > 0 ? sumValues(this.selectedOrders, 'volume') : this.totalVolume;
		},
		displayedWeight() {
			return this.rowsSelected > 0 ? sumValues(this.selectedOrders, 'grossWeight') : this.totalWeight;
		},
		displayedLoadingMeters() {
			return this.rowsSelected > 0 ? sumValues(this.selectedOrders, 'loadingMeters') : this.totalLoadingMeters;
		},
		displayedTotalPrice() {
			return this.rowsSelected > 0 ? sumValues(this.selectedOrders, 'totalPrice') : this.totalPrice;
		},
	},
	watch: {
		ordersLoading: {
			handler(newVal) {
				if (newVal === true && this.rowsSelected > 0 && this.$refs.grid) {
					this.$refs.grid.resetSelectedRows();
				}
			},
			immediate: true
		}
	},
	async mounted() {
		await this.$nextTick();
		let map = document.querySelector('.map');
		if (map) {
			let setMapPosition = function() {
				let { x } = map.getBoundingClientRect();
				this.mapPosition = `position: fixed; display: flex; flex-wrap: no-wrap; right: ${x}; z-index: 999; background: white; padding: 14px; height:22px;`;
			}.bind(this);

			this.observer = new ResizeObserver(() => setMapPosition);
			this.observer.observe(map);
			setMapPosition();
		}

		let { subHeader } = this.$refs;
		if (subHeader) {
			this.subHeaderObserver = new ResizeObserver(() => {
				let { width } = subHeader.getBoundingClientRect();
				this.subHeaderWidth = width;
			});
			this.subHeaderObserver.observe(subHeader);
		}
	},
	beforeDestroy() {
		if (this.observer) this.observer.disconnect();
		if (this.subHeaderObserver) this.subHeaderObserver.disconnect();
	},
	methods: {
		...mapDynamicActions('computedStoreNamespace', [ 'geocodeOrder', 'createParallelSubOrder' ]),
		...mapDynamicActions('computedLoadPlanningNamespace',[
			'setSelectedGridOrderIds',
			'setFilteredGridOrderIds',
			'addOrdersToTrip',
			'addOrdersToTransporter',
			'getWaybill',
			'getPackageLabel',
		]),
		toggleOrderList() {
			this.$emit('toggleOrderList');
		},
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
			this.setSelectedGridOrderIds(ids);
		},
		onFiltered(ids) {
			this.setFilteredGridOrderIds(ids);
		},
		onAddToTransporter() {
			this.$emit('showAddToTransporter', { trip: this.tripDetails, orders: this.selectedOrders });
		},
		onAddToTrip() {
			this.$emit('showAddToTrip', { trip: this.tripDetails, orders: this.selectedOrders });
		},
		openNewOrderModal() {
			eventBus.$emit('toggleNewOrderModal',true);
		},
		async onGeocodeOrder(order) {
			try {
				await this.geocodeOrder(order);
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToGeocode')
				});
			}
		},
		showWaybill() {
			let ids = this.selectedOrders.map(x => x.orderId);
			this.getWaybill(ids);
		},
		showSingleWaybill(id) {
			this.getWaybill([id]);
		},
		showPackageLabel() {
			let ids = this.selectedOrders.map(x => x.orderId);
			this.getPackageLabel(ids);
		},
		showSinglePackageLabel(id) {
			this.getPackageLabel([id]);
		},
		async onCreateParallelSubOrder(subOrderId) {
			try {
				await this.createParallelSubOrder(subOrderId);
				this.$notify({
					type: 'success',
					title: this.$t('parallelSubOrderCreated'),
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('unableToCreateParallelSubOrder'),
					duration: -1
				});
			}
		},
	}
};
</script>


<style lang="scss" scoped>
.order-list {
	.order-list-header {
		height: 100%;
		display:flex;
		align-items: center;
		padding-left: 15px;
	}
	.order-list-panel-header {
		width: 145px;
	}
	.order-list-summary {
		display:grid;
		padding: 15px;
		grid-template-columns: repeat(4, max-content);
		grid-column-gap: 10px;
		margin-right: auto;
		width: 225px;
	}
	.order-list-sub-header {
		height: 100%;
		.buttons-with-text {
			display:grid;
			padding: 0px 10px;
			height: 100%;
			align-items: center;
			grid-template-columns: repeat(3, minmax(100px, max-content)) auto repeat(2, minmax(100px, max-content));
			grid-column-gap: 10px;
		}
		.buttons-no-text {
			display:grid;
			padding: 0px 10px;
			height: 100%;
			align-items: center;
			grid-template-columns: repeat(3, minmax(min-content, max-content)) auto repeat(2, minmax(min-content, max-content));
			grid-column-gap: 10px;
		}
	}
	.order-list-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px
	}
}
</style>
